<script setup lang="ts">
/**
 * TheNavbar - Main navigation component
 * ======================================
 * Responsive navbar with search, cart, and user menu.
 */

import { ref, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'

const { t } = useI18n()

const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()

// Mobile menu state
const isMobileMenuOpen = ref(false)
const isSearchOpen = ref(false)
const isUserMenuOpen = ref(false)
const searchQuery = ref('')

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated)
const isAdmin = computed(() => authStore.isAdmin)
const cartCount = computed(() => cartStore.itemCount)
const userName = computed(() => authStore.fullName)

// Methods
function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (isMobileMenuOpen.value) {
    isSearchOpen.value = false
    isUserMenuOpen.value = false
  }
}

function toggleSearch() {
  isSearchOpen.value = !isSearchOpen.value
  if (isSearchOpen.value) {
    isMobileMenuOpen.value = false
  }
}

function toggleUserMenu() {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

function closeMenus() {
  isMobileMenuOpen.value = false
  isSearchOpen.value = false
  isUserMenuOpen.value = false
}

function handleSearch() {
  if (searchQuery.value.trim()) {
    router.push({ name: 'search', query: { q: searchQuery.value } })
    searchQuery.value = ''
    closeMenus()
  }
}

async function handleLogout() {
  await authStore.logout()
  router.push({ name: 'home' })
  closeMenus()
}

function goHome() {
  closeMenus()
  router.push({ name: 'home' })
}

// Navigation links (computed to react to language changes)
const navLinks = computed(() => [
  { name: t('nav.shop'), route: 'products' },
  { name: t('nav.collections'), route: 'collections' },
  { name: t('nav.about'), route: 'about' },
  { name: t('nav.blog'), route: 'blog' },
  { name: t('nav.contact'), route: 'contact' },
])
</script>

<template>
  <header class="sticky top-0 z-50 bg-white shadow-sm">
    <nav class="container-custom">
      <div class="flex items-center justify-between h-16 lg:h-20">
        <!-- Logo -->
        <button
          @click="goHome"
          class="flex-shrink-0 cursor-pointer"
        >
          <span class="font-heading text-2xl lg:text-3xl text-primary-500 font-semibold">
            Aura by Alexia
          </span>
        </button>

        <!-- Desktop Navigation -->
        <div class="hidden lg:flex items-center space-x-8">
          <RouterLink
            v-for="link in navLinks"
            :key="link.name"
            :to="{ name: link.route }"
            class="text-charcoal hover:text-primary-500 font-medium transition-colors"
          >
            {{ link.name }}
          </RouterLink>
        </div>

        <!-- Right side icons -->
        <div class="flex items-center space-x-4">
          <!-- Language Switcher -->
          <LanguageSwitcher />

          <!-- Search button -->
          <button
            @click="toggleSearch"
            class="p-2 text-charcoal hover:text-primary-500 transition-colors"
            :aria-label="$t('nav.search')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          <!-- User menu -->
          <div class="relative">
            <button
              @click="toggleUserMenu"
              class="flex items-center gap-2 p-2 text-charcoal hover:text-primary-500 transition-colors"
              :aria-label="$t('nav.account')"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span v-if="isAuthenticated" class="hidden sm:inline text-sm font-medium">
                {{ userName }}
              </span>
            </button>

            <!-- User dropdown -->
            <Transition name="fade">
              <div
                v-if="isUserMenuOpen"
                class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50"
              >
                <template v-if="isAuthenticated">
                  <div class="px-4 py-2 text-sm text-gray-500 border-b">
                    {{ userName }} (admin: {{ authStore.user?.is_admin }})
                  </div>
                  <!-- Admin Panel Link -->
                  <RouterLink
                    v-if="authStore.user?.is_admin"
                    :to="{ name: 'admin-dashboard' }"
                    class="block px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 font-medium"
                    @click="closeMenus"
                  >
                    {{ $t('nav.admin_panel') }}
                  </RouterLink>
                  <RouterLink
                    :to="{ name: 'account' }"
                    class="block px-4 py-2 text-sm text-charcoal hover:bg-gray-50"
                    @click="closeMenus"
                  >
                    {{ $t('account.my_account') }}
                  </RouterLink>
                  <RouterLink
                    :to="{ name: 'orders' }"
                    class="block px-4 py-2 text-sm text-charcoal hover:bg-gray-50"
                    @click="closeMenus"
                  >
                    {{ $t('nav.my_orders') }}
                  </RouterLink>
                  <RouterLink
                    :to="{ name: 'wishlist' }"
                    class="block px-4 py-2 text-sm text-charcoal hover:bg-gray-50"
                    @click="closeMenus"
                  >
                    {{ $t('nav.wishlist') }}
                  </RouterLink>
                  <button
                    @click="handleLogout"
                    class="w-full text-left px-4 py-2 text-sm text-charcoal hover:bg-gray-50 border-t"
                  >
                    {{ $t('nav.logout') }}
                  </button>
                </template>
                <template v-else>
                  <RouterLink
                    :to="{ name: 'login' }"
                    class="block px-4 py-2 text-sm text-charcoal hover:bg-gray-50"
                    @click="closeMenus"
                  >
                    {{ $t('nav.login') }}
                  </RouterLink>
                  <RouterLink
                    :to="{ name: 'register' }"
                    class="block px-4 py-2 text-sm text-charcoal hover:bg-gray-50"
                    @click="closeMenus"
                  >
                    {{ $t('nav.register') }}
                  </RouterLink>
                </template>
              </div>
            </Transition>
          </div>

          <!-- Cart button - Made more visible -->
          <button
            @click="cartStore.toggleMiniCart"
            class="relative p-3 rounded-full transition-all hover:scale-110"
            style="background-color: #D4AF37;"
            :aria-label="$t('nav.cart')"
          >
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span
              v-if="cartCount > 0"
              class="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs
                     rounded-full flex items-center justify-center font-bold"
            >
              {{ cartCount > 9 ? '9+' : cartCount }}
            </span>
          </button>

          <!-- Mobile menu button -->
          <button
            @click="toggleMobileMenu"
            class="lg:hidden p-2 text-charcoal hover:text-primary-500 transition-colors"
            :aria-label="$t('nav.menu')"
          >
            <svg v-if="!isMobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Search bar (expandable) -->
      <Transition name="slide-down">
        <div v-if="isSearchOpen" class="py-4 border-t">
          <form @submit.prevent="handleSearch" class="flex gap-2">
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="$t('common.search_products')"
              class="input flex-grow"
              autofocus
            />
            <button type="submit" class="btn-primary">
              {{ $t('nav.search') }}
            </button>
          </form>
        </div>
      </Transition>

      <!-- Mobile menu -->
      <Transition name="slide-down">
        <div v-if="isMobileMenuOpen" class="lg:hidden py-4 border-t">
          <div class="flex flex-col space-y-4">
            <RouterLink
              v-for="link in navLinks"
              :key="link.name"
              :to="{ name: link.route }"
              class="text-charcoal hover:text-primary-500 font-medium py-2"
              @click="closeMenus"
            >
              {{ link.name }}
            </RouterLink>
          </div>
        </div>
      </Transition>
    </nav>
  </header>
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
